<template>
  <BasePopUp v-if="showModal" @detailsClosed="closeConfirmRequested">
    <div v-if="order" class="modal-body-container">
      <!-- Title -->
      <h4 class="text-center mt-4">{{ $t("orderDetail.title") }} #{{order.id}}</h4>
      <div class="d-flex flex-column px-3">
        <!-- Editable information (status) -->
        <form class="d-flex flex-column">
          <!-- Status -->
          <div role="group" class="form-group flex-grow-1">
            <label class="m-0"> {{ $t('status') }}</label>
            <SingleOptionDropdown v-model="status" :options="statusOptions" :title="$t('status')" :index="0"></SingleOptionDropdown>
          </div>
          <!-- Action button + submitting -->  
          <CSpinner v-if="submitting" variant="success" small></CSpinner>
          <CButton v-else type="button" class="btn btn-primary" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
        </form>
        <!-- User information (user + addresses) -->
        <div class="d-flex gap-1">
          <h5 class="my-3 font-weight-bold">{{$t("userInfo")}}</h5>
          <AccordionArrow v-model="showAddresses"></AccordionArrow>
        </div>
        <p><span class="font-weight-bold">{{ $t("orderDetail.name") }}: </span>{{ order.billingAddress.email }}</p>
        <p><span class="font-weight-bold">{{ $t("orderDetail.email") }}: </span>{{ order.billingAddress.firstName }}</p>
        <div id="address-box" v-if="showAddresses" class="d-flex flex-wrap justify-content-between p-3">
          <div v-if="order.billingAddress">
            <h5 class="mb-4 text-left font-weight-bold">{{ $t("address.invoice") }}</h5>
            <p v-if="order.billingAddress.firstName" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.firstname") }}: </span>{{order.billingAddress.firstName}}</p>
            <p v-if="order.billingAddress.lastName" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.lastname") }}: </span>{{order.billingAddress.lastName}}</p>
            <p v-if="order.billingAddress.email" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.email") }}: </span>{{order.billingAddress.email}}</p>
            <p v-if="order.billingAddress.phoneNumber" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.phone") }}: </span>{{order.billingAddress.phoneNumber}}</p>
            <p v-if="order.billingAddress.nationality" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.nationality") }}: </span>{{order.billingAddress.nationality}}</p>
            <p v-if="order.billingAddress.vat" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.nif") }}: </span>{{order.billingAddress.vat}}</p>
            <p v-if="order.billingAddress.address" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.address") }}: </span>{{order.billingAddress.address}}</p>
            <p v-if="order.billingAddress.zipCode" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.zip") }}: </span>{{order.billingAddress.zipCode}}</p>
            <p v-if="order.billingAddress.city" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.city") }}: </span>{{order.billingAddress.city}}</p>
            <p v-if="order.billingAddress.country" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.country") }}: </span>{{order.billingAddress.country}}</p>
          </div>
          <div v-if="order.shippingAddress">
            <h5 class="mb-4 text-left font-weight-bold">{{ $t("address.shipping") }}</h5>
            <p v-if="order.shippingAddress.firstName" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.firstname") }}: </span>{{order.shippingAddress.firstName}}</p>
            <p v-if="order.shippingAddress.lastName" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.lastname") }}: </span>{{order.shippingAddress.lastName}}</p>
            <p v-if="order.shippingAddress.email" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.email") }}: </span>{{order.shippingAddress.email}}</p>
            <p v-if="order.shippingAddress.phoneNumber" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.phone") }}: </span>{{order.shippingAddress.phoneNumber}}</p>
            <p v-if="order.shippingAddress.nationality" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.nationality") }}: </span>{{order.shippingAddress.nationality}}</p>
            <p v-if="order.shippingAddress.vat" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.nif") }}: </span>{{order.shippingAddress.vat}}</p>
            <p v-if="order.shippingAddress.address" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.address") }}: </span>{{order.shippingAddress.address}}</p>
            <p v-if="order.shippingAddress.zipCode" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.zip") }}: </span>{{order.shippingAddress.zipCode}}</p>
            <p v-if="order.shippingAddress.city" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.city") }}: </span>{{order.shippingAddress.city}}</p>
            <p v-if="order.shippingAddress.country" class="mb-2 text-left"><span class="font-weight-bold">{{ $t("address.country") }}: </span>{{order.shippingAddress.country}}</p>
          </div>
        </div>
        <!-- Product information -->
        <h5 class="my-3 font-weight-bold">{{$t("products")}}:</h5>
        <div class="container" v-for="(productDetail, index) in order.products" :key="`product-${index}`">
          <div class="d-block">
            <span class="font-weight-bold">{{ $t("orderDetail.title") }}:</span> <span>{{ productDetail.product.title }}</span>
            <br>
            <span class="font-weight-bold">{{ $t("orderDetail.price") }}:</span> <span>{{ productDetail.product.finalPrice }}€</span>
          </div>
          <!-- Cart value -->
            <div class="d-block mt-2">
              <span class="font-weight-bold">{{ $t("orderDetail.quantity") }}:</span> <span>{{ productDetail.quantity }}</span>
            </div>
          <h5 class="my-3 font-weight-bold">{{$t("customFieldDetail")}}:</h5>
          <div v-for="(customFieldDetail, index) in productDetail.customFields" :key="index">
            <div class="my-2 d-block">
              <span class="font-weight-bold">{{ $t("orderDetail.name") }}:</span> <span >{{ customFieldDetail.name }}</span>
              <br>
              <span class="font-weight-bold">{{ $t("orderDetail.value") }}:</span> <span >{{ customFieldDetail.value }}</span>
              <br>
              <span class="font-weight-bold" v-if="customFieldDetail.price">{{ $t("orderDetail.price") }}:</span> <span v-if="customFieldDetail.price">{{ customFieldDetail.price }}€</span>
            </div>
          </div>
        </div>
      </div>
      <!-- Button -->
      <div class="d-flex justify-content-center mt-5">
        <CButton type="button" class="btn btn-primary m-auto" @click="closeConfirmRequested">{{ $t("close") }}</CButton>
      </div>
    </div>
  </BasePopUp>
</template>

<script>
import BasePopUp from "./BasePopUp.vue";
import { convertToCamelCase, formatDate, formatDateTime } from "@/helpers/utils.js";
import { mapState, mapActions } from 'vuex'
import SingleOptionDropdown from '@/controls/SingleOptionDropdown.vue'
import AccordionArrow from '@/controls/AccordionArrow.vue'
import { API_HELPER } from "@/helpers/api.js";

export default {
  extends: BasePopUp,
  components: { BasePopUp, SingleOptionDropdown, AccordionArrow },
  props: {
    order: Object,
  },
  data() {
    return {
      status: "PendingPayment",
      colors: {},
      images: {},
      statusOptions: [{label: this.$t("orderStatus.pendingPayment"), key: "PendingPayment"}, {label: this.$t("orderStatus.processing"), key: "Processing"}, {label: this.$t("orderStatus.sent"), key: "Sent"}, {label: this.$t("orderStatus.concluded"), key: "Concluded"}, {label: this.$t("orderStatus.canceled"), key: "Canceled"} ],
      showAddresses: false,
      selectedProduct: null,
    };
  },
  computed:
  {
    ...mapState('orders', ['putOrderStatusOutput']),
    submitting: function()
    {
      return this.putOrderStatusOutput === null;
    },
  },
  methods: {
    convertBase64ToImage(base64)
    {
      let base = 'data:image/jpeg;base64,' + base64
      // convert base64 to raw binary data held in a string
      // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
      let byteString = atob(base.split(',')[1]);

      // separate out the mime component
      let mimeString = base.split(',')[0].split(':')[1].split(';')[0]

      // write the bytes of the string to an ArrayBuffer
      let ab = new ArrayBuffer(byteString.length);

      // create a view into the buffer
      let ia = new Uint8Array(ab);

      // set the bytes of the buffer to the correct values
      for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
      }

      // write the ArrayBuffer to a blob, and you're done
      let blob = new Blob([ab], {type: mimeString});
      let fileSaver = require('file-saver');
      fileSaver.saveAs(blob);

    },
    closeConfirmRequested() {
      this.$emit("closeConfirmRequested");
    },
    formatDateTime,
    ...mapActions('orders', ['putOrderStatus']),
    submitClicked()
    {
      const {status} = this;
      const id = this.order.id;

      this.putOrderStatus({orderId: id, status});
    },
    convertToCamelCase
  },
  watch:
  {
    selectedProduct(val)
    {
      if(val != null)
      {
        //colors
        Object.assign(this.colors, val.colors);
        //images
        Object.assign(this.images, val.images);
      }
    },
    putOrderStatusOutput: function(val)
    {
      if(val)
      {
        this.$emit("update-order", this.order.id);
      }
    }
  }
};
</script>

<style scoped>
.modal-body-container {
  background-color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 10px;
  border-radius: 10px;
  min-height: 200px;
  min-width: 620px;
}

#address-box
{
  border: 1px solid lightslategray;
}
</style>